import UntitledIcon, { TIconName } from '@payaca/untitled-icons';
import { clstx } from '@payaca/utilities/cvaMerge';
import React from 'react';
import { Box, PolymorphicComponentProps } from 'react-polymorphic-box';
import UntitledLoading from '../UntitledLoading/UntitledLoading';

export interface MenuItemCoreBaseProps {
  disabled?: boolean;
  isProcessing?: boolean;
  isSelected?: boolean;
  disabledReasons?: string[];
  wrapperClassName?: string;
  iconName?: TIconName;
}

export type TProps<E extends React.ElementType> = PolymorphicComponentProps<
  E,
  MenuItemCoreBaseProps
>;

const defaultElement = 'button';

const MenuItemCore: <E extends React.ElementType = typeof defaultElement>(
  props: TProps<E>
) => React.ReactNode | null = React.forwardRef(
  <E extends React.ElementType = typeof defaultElement>(
    {
      children,
      wrapperClassName,
      iconName,
      className,
      isProcessing = false,
      isSelected = false,
      disabled,
      disabledReasons,
      ...rest
    }: TProps<E>,
    ref: typeof rest.ref
  ) => {
    return (
      // @ts-expect-error — react-polymorphic-box is not compatible with TypeScript v5
      <Box
        as={defaultElement}
        className={clstx(
          'flex w-full text-left cursor-pointer items-center gap-x-3.5 rounded-lg px-3 py-2 text-base bg-transparent',
          'hover:not-data-disabled:bg-gray-100 hover:not-disabled:bg-gray-100',
          'data-disabled:cursor-not-allowed data-disabled:opacity-50 disabled:cursor-not-allowed disabled:opacity-50',
          'data-focus:bg-gray-100 focus:bg-gray-100 focus:outline-none',
          'data-active:bg-gray-100',
          wrapperClassName
        )}
        disabled={disabled}
        ref={ref}
        {...rest}
      >
        <div className="min-w-0 flex-1">
          <div className="flex gap-2.5 items-center">
            {iconName && <UntitledIcon name={iconName} className="size-5" />}

            {typeof children === 'string' ? (
              <p className={className}>{children}</p>
            ) : (
              children
            )}
          </div>

          {disabled && disabledReasons && disabledReasons.length > 0 && (
            <>
              {disabledReasons.length === 1 ? (
                <p className="supporting-body max-w-40">{disabledReasons[0]}</p>
              ) : (
                <ul className="supporting-body max-w-40 list-disc pl-4">
                  {disabledReasons.map((reason, idx) => (
                    <li key={`${reason}-${idx}`}>{reason}</li>
                  ))}
                </ul>
              )}
            </>
          )}
        </div>

        {isProcessing && (
          <UntitledLoading className="ml-auto h-4 w-4 shrink-0" />
        )}
        {isSelected && (
          <UntitledIcon
            name="check"
            className="ml-auto h-3.5 w-3.5 shrink-0 text-blue-600"
          />
        )}
      </Box>
    );
  }
);

export default MenuItemCore;
